import 'react-dates/initialize';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import SiteHeader from '@wix/da-shared-react/pkg/SiteHeader';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import { NotificationQueueItem } from '@wix/da-shared-react/pkg/Notifications';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import SisuFunnelBanner from '@wix/da-shared-react/pkg/SisuFunnelBanner';
import { Pages } from '../../../constants';
import classnames from 'classnames';
import ShowLessNotification from '../ShowLessNotification';
import { ModalType, NotificationType } from '../../types/notification';
import Page from '../Page';
import PageNetworkbar from '../Page.networkbar';
import GroupClosedWatchModal from '../GroupModals/GroupClosedWatchModal';
import GroupJoinPendingModal from '../GroupModals/GroupJoinPendingModal';
import s from './App.scss';

export interface Props {
  language: string;
  isNetworkBarPage?: boolean;
  page: Pages;
  environment?: RuntimeEnvironment;
  seoNavigation: {
    next?: string;
    prev?: string;
  };
  activeSiteHeaderItem?: string;
  sisuFunnelBannerVisible?: boolean;
  showNetworkBarInSiteHeader?: boolean;
}

const renderNotification = (notification: NotificationQueueItem) => {
  switch (notification.type) {
    case NotificationType.SHOW_LESS:
      return (
        <ShowLessNotification
          id={notification.id}
          queueName={notification.queueName}
          type={notification.type}
          {...notification.params}
        />
      );
    default:
      return null;
  }
};

const renderModal = (modalType: string, popModal: () => void, params?: any) => {
  switch (modalType) {
    case ModalType.GROUP_CLOSED_WATCH:
      return <GroupClosedWatchModal popModal={popModal} group={params.group} />;
    case ModalType.GROUP_JOIN_PENDING:
      return <GroupJoinPendingModal popModal={popModal} group={params.group} />;
    default:
      return null;
  }
};

const App: React.FC<Props> = ({
  page,
  environment,
  language,
  seoNavigation: { next, prev },
  sisuFunnelBannerVisible,
  showNetworkBarInSiteHeader,
  isNetworkBarPage,
  activeSiteHeaderItem,
}) => {
  const isSearch = page === Pages.Search;
  const isTagPage = page === Pages.Tag;

  return (
    <DaApp
      language={language}
      environment={environment}
      backToTopOffset={0}
      notificationsRenderFunc={renderNotification}
      modalsRenderFunc={renderModal}
    >
      <Helmet>
        {next && <link rel="next" href={next} />}
        {prev && <link rel="prev" href={prev} />}
        {isSearch && <meta name="robots" content="noindex" />}
      </Helmet>
      <ErrorBoundary>
        <SiteHeader
          environment={environment}
          showNetworkBar={showNetworkBarInSiteHeader}
          activeMainMenuItem={activeSiteHeaderItem}
        />
        <div
          className={classnames(
            s['content-container'],
            isTagPage && s['tag-container']
          )}
        >
          {isNetworkBarPage ? <PageNetworkbar /> : <Page />}
        </div>
        <SisuFunnelBanner visible={sisuFunnelBannerVisible} />
      </ErrorBoundary>
    </DaApp>
  );
};
App.displayName = 'App';

export default App;
